import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import DaasTableRowCell from '@/components/table/DaasTableRowCell';
import SiEdit from '@/icons/SiEdit';
import { Product } from '@/services/api/contracts/catalog/types';
import { useGetRateCards } from '@/services/api/hooks/catalog';
import { usePrintCurrencyString } from '@/utils/price';
import { useMemo, useState } from 'react';
import { RateCardWizardModal } from './RateCardWizardModal';
import { SERVICE_REQUEST_TYPES } from '@/constants/serviceRequests';
import { SortDirection } from '@/components/table/sortHelper';
import useCSVDownload from '@/services/csv/useCSVDownload';
import { Upload, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const getSrLabel = (sr: string) => {
  if (!sr || sr === 'None') return 'None';
  return SERVICE_REQUEST_TYPES.find(t => t.value === sr)?.label || sr;
};

export default function RateCardManagement() {
  const [rateCards] = useGetRateCards();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Product | null>(null);
  const navigate = useNavigate();
  
  const printCurrency = usePrintCurrencyString();
  const { downloadCSV } = useCSVDownload();

  const handleExportCSV = () => {
    if (!rateCards?.items) return;

    const headers = [
      'Name',
      'Product ID',
      'Region',
      'Price',
      'Applicable Request Types',
      'Base Pack For',
      'Item Rate For',
      'Max Chargeable Weight (kg)',
      'Is Item Rate Override',
      'Is Exclusive Crate',
      'Add-On Category',
      'Covered By Warranty'
    ];

    const csvData = rateCards.items.map(card => {
      let priceStr = 'N/A';
      if (card.price?.price !== undefined) {
        priceStr = (card.price.price / 100).toFixed(2);
      }

      return {
        'Name': card.name,
        'Product ID': card.productId,
        'Region': card.region?.code || 'N/A',
        'Price': priceStr,
        'Applicable Request Types': card.propertyBag?.applicableRequestTypes
          ?.map(t => getSrLabel(t))
          .join(' | ') || 'None',
        'Base Pack For': getSrLabel(card.propertyBag?.basePackForRequestType || 'None'),
        'Item Rate For': getSrLabel(card.propertyBag?.itemRateForRequestType || 'None'),
        'Max Chargeable Weight (kg)': card.propertyBag?.maxChargeableWeightKg ?? 'None',
        'Is Item Rate Override': card.propertyBag?.isItemRateOverride ? 'Yes' : 'No',
        'Is Exclusive Crate': card.propertyBag?.isExclusiveOverride ? 'Yes' : 'No',
        'Add-On Category': card.propertyBag?.addonCategory || 'None',
        'Covered By Warranty': card.propertyBag?.isCoveredByWarranty ? 'Yes' : 'No'
      };
    });

    downloadCSV('Rate_Cards', headers, csvData);
  };

  const [sortColumnId, setSortColumnId] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);

  const headers: DaasTableColumn[] = [
    { accessor: 'name', text: 'Name', size: '1.5fr', sortable: true },
    { accessor: 'price', text: 'Price', size: '0.75fr', sortable: true },
    { accessor: 'region', text: 'Region', size: 'max-content', sortable: true },
    { accessor: 'productId', text: 'SKU', size: '1.25fr', sortable: true },
    { accessor: 'applicableRequestTypes', text: 'Request Types', size: 'max-content' },
    { accessor: 'basePackType', text: 'Base Rate', sortable: true },
    { accessor: 'itemRateForRequestType', text: 'Item Rate', sortable: true },
    { accessor: 'maxChargeableWeightKg', text: 'Max Kg', sortable: true },
    { accessor: 'actions', text: '', size: 'max-content' }
  ];

  const handleEdit = (card: Product) => {
    setEditingCard(card);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setEditingCard(null);
    setIsFormOpen(true);
  };

  const sortedCards = useMemo(() => {
    if (!rateCards?.items) return [];
    const items = [...rateCards.items];
    items.sort((a, b) => {
      let valA: any = '';
      let valB: any = '';
      switch (sortColumnId) {
        case 'name':
          valA = a.name?.toLowerCase() || '';
          valB = b.name?.toLowerCase() || '';
          break;
        case 'price':
          valA = a.price?.price || 0;
          valB = b.price?.price || 0;
          break;
        case 'region':
          valA = a.region?.code?.toLowerCase() || '';
          valB = b.region?.code?.toLowerCase() || '';
          break;
        case 'productId':
          valA = a.productId?.toLowerCase() || '';
          valB = b.productId?.toLowerCase() || '';
          break;
        case 'basePackType':
          valA = getSrLabel(a.propertyBag?.basePackForRequestType || 'None').toLowerCase();
          valB = getSrLabel(b.propertyBag?.basePackForRequestType || 'None').toLowerCase();
          break;
        case 'itemRateForRequestType':
          valA = getSrLabel(a.propertyBag?.itemRateForRequestType || 'None').toLowerCase();
          valB = getSrLabel(b.propertyBag?.itemRateForRequestType || 'None').toLowerCase();
          break;
        case 'maxChargeableWeightKg':
          valA = a.propertyBag?.maxChargeableWeightKg || 0;
          valB = b.propertyBag?.maxChargeableWeightKg || 0;
          break;
      }
      
      if (valA < valB) return sortDirection === SortDirection.ASC ? -1 : 1;
      if (valA > valB) return sortDirection === SortDirection.ASC ? 1 : -1;
      return 0;
    });
    return items;
  }, [rateCards, sortColumnId, sortDirection]);

  return (
    <div className="flex flex-col gap-4 p-8 w-full max-w-7xl mx-auto h-full relative overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Rate Cards</h1>
        <div className="flex gap-3 items-center">
          <Button variant={ButtonVariant.Secondary} onClick={() => navigate('/admin/rate-cards/bulk-import')}>
            <Upload className="w-4 h-4 mr-2" />
            Import Rates
          </Button>
          <Button variant={ButtonVariant.Secondary} className="bg-[#8ADB53] hover:bg-[#7bc449] text-gray-900 border-none" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export Rates
          </Button>
          <Button onClick={handleCreate}>Create Rate Card</Button>
        </div>
      </div>

      <DaaSTable 
        headers={headers}
        sortColumnId={sortColumnId}
        sortDirection={sortDirection}
        onSortCallback={(columnId, direction) => {
          setSortColumnId(columnId);
          setSortDirection(direction);
        }}
      >
        {sortedCards.map(card => (
          <div
            key={card.id}
            className="contents cursor-pointer hover:bg-gray-50 transition-colors text-sm"
            onClick={() => handleEdit(card)}
          >
            <DaasTableRowCell className="truncate text-left justify-start">{card.name}</DaasTableRowCell>
            <DaasTableRowCell className="truncate">
              {card.price?.price !== undefined ? printCurrency(card.price.price, card.region?.code) : 'N/A'}
            </DaasTableRowCell>
            <DaasTableRowCell className="truncate">{card.region?.code}</DaasTableRowCell>
            <DaasTableRowCell className="truncate">{card.productId}</DaasTableRowCell>
            <DaasTableRowCell>
              {card.propertyBag?.applicableRequestTypes && card.propertyBag.applicableRequestTypes.length > 0 ? (
                <div className="flex flex-col items-start w-full overflow-hidden">
                  {card.propertyBag.applicableRequestTypes.map((t, i) => (
                    <div key={i} className="truncate w-full">{getSrLabel(t)}</div>
                  ))}
                </div>
              ) : (
                'N/A'
              )}
            </DaasTableRowCell>
            <DaasTableRowCell className="truncate">{getSrLabel(card.propertyBag?.basePackForRequestType || 'None')}</DaasTableRowCell>
            <DaasTableRowCell className="truncate">{getSrLabel(card.propertyBag?.itemRateForRequestType || 'None')}</DaasTableRowCell>
            <DaasTableRowCell className="truncate">{card.propertyBag?.maxChargeableWeightKg ? `${card.propertyBag.maxChargeableWeightKg} kg` : 'None'}</DaasTableRowCell>
            <DaasTableRowCell>
              <Button 
                variant={ButtonVariant.Ghost}
                className="text-gray-500 hover:text-gray-700 p-2 h-auto"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handleEdit(card); 
                }}
              >
                <SiEdit className="w-5 h-5" />
              </Button>
            </DaasTableRowCell>
          </div>
        ))}
      </DaaSTable>

      <RateCardWizardModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        editingCard={editingCard}
      />
    </div>
  );
}
